/**
 * @fileoverview General helper utilities
 * @module utils/helpers
 */

import Swal from 'sweetalert2';
import { translateMessage, translateTitle } from './backendMessage';

/**
 * Generate random alphanumeric string
 * @param {number} length - Length of string to generate
 * @returns {string} Random string
 *
 * @example
 * random_charactor(8) // "a1B2c3D4"
 */
export const random_charactor = (length) => {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  return Array.from({ length }, () =>
    characters.charAt(Math.floor(Math.random() * characters.length))
  ).join('');
};

/**
 * Copy text to clipboard
 * @param {string} textToCopy - Text to copy
 * @returns {Promise} Resolves when copied
 */
export const copyText = (textToCopy) => {
  return navigator.clipboard
    .writeText(textToCopy)
    .then(() => {
      Swal.fire({
        icon: 'success',
        title: translateTitle('Success'),
        text: translateMessage('Copied successfully'),
        showConfirmButton: false,
        timer: 1500,
        background: '#fff',
        color: '#000000',
        confirmButtonColor: '#212529',
        cancelButtonColor: 'unset',
      });
    })
    .catch((err) => {
      console.error('Failed to copy:', err);
    });
};

/**
 * Get URL query parameter
 * @param {string} name - Parameter name
 * @returns {string|null} Parameter value
 *
 * @example
 * // URL: /page?id=123
 * params('id') // "123"
 */
export const params = (name) => {
  const queryParams = new URLSearchParams(window.location.search);
  return queryParams.get(name);
};

/**
 * Required field indicator JSX
 */
export const required = (<span class="text-danger">*</span>);

/**
 * Debounce function execution
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} Debounced function
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Throttle function execution
 * @param {Function} func - Function to throttle
 * @param {number} limit - Time limit in milliseconds
 * @returns {Function} Throttled function
 */
export const throttle = (func, limit) => {
  let inThrottle;
  return function executedFunction(...args) {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

/**
 * Sleep/delay function
 * @param {number} ms - Milliseconds to wait
 * @returns {Promise} Resolves after delay
 */
export const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Check if value is empty (null, undefined, empty string, empty array, empty object)
 * @param {*} value - Value to check
 * @returns {boolean} True if empty
 */
export const isEmpty = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
};

/**
 * ตรวจว่ารายการ master นี้ยัง "เปิดใช้งาน" อยู่หรือไม่
 *
 * - `is_active === false` -> ปิดใช้งาน (ใช้กับทุกตาราง master)
 * - `customer_status === "inactive"` -> ปิดใช้งาน (ลูกค้ามีสถานะแยกอีกชั้น)
 *   หมายเหตุ: "HOLD" ยังถือว่าเปิดใช้งานอยู่ เพราะ backend ตั้งใจให้เปิดใบแจ้งซ่อมได้
 *   (มี logic บังคับ priority = "Hold" รออยู่ใน customer.service.js)
 *
 * @param {object} item - รายการจาก API
 * @returns {boolean} true ถ้ายังเลือกใช้งานได้
 */
export const isOptionActive = (item) => {
  if (!item || typeof item !== 'object') return true;

  const customerStatus =
    typeof item.customer_status === 'string' ? item.customer_status.trim().toLowerCase() : '';

  // ลูกค้าที่ตั้งสถานะเป็น "inactive" ถือว่าปิดใช้งาน ไม่ว่า is_active จะเป็นอะไร
  if (customerStatus === 'inactive') return false;

  // "hold" ยังเปิดใบแจ้งซ่อมได้ (backend บังคับ priority = "Hold" ให้เอง) จึงยังต้องเลือกได้
  // ต้องยกเว้นไว้ตรงนี้ เพราะ Import Center เซ็ต is_active = false ให้ลูกค้า hold ด้วย
  // (customer.service.js: statusActive = customerStatus === "active") ต่างจากฟอร์มบนเว็บที่เซ็ต true
  if (customerStatus === 'hold') return true;

  if (item.is_active === false) return false;
  return true;
};

/** คำว่า "ปิดใช้งาน" ตามภาษาปัจจุบัน (helpers.js เรียก t() ไม่ได้ เพราะจะ import วน) */
const inactiveWord = () =>
  (localStorage.getItem('lang') || 'TH') === 'EN' ? 'Inactive' : 'ปิดใช้งาน';

const toKeyList = (value) =>
  (Array.isArray(value) ? value : [value])
    .filter((v) => v !== null && v !== undefined && v !== '')
    .map(String);

/**
 * กรองรายการที่ปิดใช้งานออกจาก dropdown/select
 *
 * เหตุผลที่ไม่กรองตั้งแต่ตอนยิง API: หลายหน้าใช้ list ก้อนเดียวกันทั้งเป็น options
 * ของ dropdown และเป็น lookup map (`.find(uid)`) สำหรับแสดงชื่อ/สี/รายละเอียด
 * ของข้อมูลเก่า ถ้ากรองที่ต้นทางข้อมูลเก่าที่อ้างอิง master ที่ถูกปิดไปแล้วจะแสดงว่าง
 * ให้เก็บ list เต็มไว้ใน state เหมือนเดิม แล้วห่อด้วยฟังก์ชันนี้เฉพาะตอนส่งเข้า `options`
 *
 * ค่าที่ถูกเลือกอยู่ (`current`) จะถูกคงไว้เสมอแม้จะปิดใช้งานไปแล้ว ไม่งั้น Select
 * จะหา label ไม่เจอ -> ช่องว่างเปล่า -> ผู้ใช้กดบันทึกแล้วค่าเดิมหายโดยไม่ตั้งใจ
 *
 * @param {Array} list - รายการทั้งหมดจาก state
 * @param {string|number|Array} [current] - ค่าที่เลือกอยู่ (รองรับ multi-select)
 * @param {object} [config]
 * @param {string[]} [config.keys=['value','uid','id']] - field ที่ใช้จับคู่กับ current
 * @param {boolean} [config.mark=true] - ต่อท้าย label ว่า "(ปิดใช้งาน)" ให้ตัวที่คงไว้
 * @param {Function} [config.isActive=isOptionActive] - เงื่อนไข active แบบกำหนดเอง
 * @returns {Array} รายการที่กรองแล้ว
 *
 * @example
 * <Select options={activeOptions(data_customer, create_customer_id)} ... />
 */
export const activeOptions = (list, current, config = {}) => {
  if (!Array.isArray(list)) return [];
  const {
    keys = ['value', 'uid', 'id'],
    mark = true,
    isActive = isOptionActive,
  } = config;

  const selected = toKeyList(current);
  const isSelected = (item) =>
    selected.length > 0 &&
    keys.some((k) => {
      const v = item?.[k];
      return v !== undefined && v !== null && selected.includes(String(v));
    });

  const result = [];
  for (const item of list) {
    if (isActive(item)) {
      result.push(item);
    } else if (isSelected(item)) {
      result.push(
        mark && item?.label
          ? { ...item, label: `${item.label} (${inactiveWord()})`, is_inactive_kept: true }
          : { ...item, is_inactive_kept: true }
      );
    }
  }
  return result;
};

/**
 * เวอร์ชันสำหรับข้อมูลที่เป็นต้นไม้ (เช่น warehouse/brands ที่มี children)
 * ตัดทั้งกิ่งเมื่อ parent ปิดใช้งาน แต่ยังคงกิ่งไว้ถ้าลูกตัวใดตัวหนึ่งเป็นค่าที่เลือกอยู่
 *
 * @param {Array} list - ต้นไม้จาก API
 * @param {string|number|Array} [current] - ค่าที่เลือกอยู่
 * @param {object} [config] - เหมือน activeOptions เพิ่ม childKey
 * @param {string} [config.childKey='children']
 * @returns {Array} ต้นไม้ที่กรองแล้ว
 */
export const activeTreeOptions = (list, current, config = {}) => {
  if (!Array.isArray(list)) return [];
  const {
    childKey = 'children',
    keys = ['value', 'uid', 'id'],
    isActive = isOptionActive,
  } = config;

  const selected = toKeyList(current);
  const isSelected = (node) =>
    selected.length > 0 &&
    keys.some((k) => {
      const v = node?.[k];
      return v !== undefined && v !== null && selected.includes(String(v));
    });

  // เก็บกิ่งที่ปิดใช้งานไว้เฉพาะเมื่อตัวมันเองหรือลูกหลานคือค่าที่เลือกอยู่
  const hasSelectedDescendant = (node) =>
    isSelected(node) ||
    (Array.isArray(node?.[childKey]) && node[childKey].some(hasSelectedDescendant));

  const walk = (nodes) =>
    nodes
      .filter((node) => isActive(node) || hasSelectedDescendant(node))
      .map((node) => {
        const children = node?.[childKey];
        const next = Array.isArray(children) ? { ...node, [childKey]: walk(children) } : { ...node };
        return isActive(node) ? next : { ...next, is_inactive_kept: true };
      });

  return walk(list);
};

// ---------------------------------------------------------------------------
// เรียงลำดับ dropdown/select: ไทย ก-ฮ ขึ้นก่อน แล้วตามด้วย a-z (ตัวเลขในข้อความเทียบแบบตัวเลข)
// ---------------------------------------------------------------------------
const thCollator = new Intl.Collator('th', { numeric: true, sensitivity: 'base' });
const enCollator = new Intl.Collator('en', { numeric: true, sensitivity: 'base' });
// อักษรไทยทั้งบล็อกรวมสระหน้า (เ แ โ ใ ไ) แต่ตัด ฿ (U+0E3F) ที่อยู่บล็อกเดียวกันออก
const THAI_LEAD = /^[\u0E01-\u0E3A\u0E40-\u0E4E]/;

/**
 * ดึงข้อความที่ใช้เรียงจาก option 1 ตัว
 * @param {*} item - string หรือ object ของ option
 * @param {string|Function} [by] - ชื่อ field หรือฟังก์ชันคืนข้อความ (ไม่ส่ง = เดาจาก label/name/name_th/...)
 */
export const optionLabel = (item, by) => {
  if (item === null || item === undefined) return '';
  if (typeof item !== 'object') return String(item);
  if (typeof by === 'function') return String(by(item) ?? '');
  if (by) return String(item[by] ?? '');
  const v =
    item.label ?? item.name ?? item.name_th ?? item.full_name ?? item.title ?? item.text ?? item.desc ?? '';
  return typeof v === 'string' ? v : String(v ?? '');
};

/** เทียบข้อความ 2 ค่า: ไทยมาก่อนอังกฤษ ในกลุ่มเดียวกันเรียงตามพจนานุกรม */
export const compareLabels = (a, b) => {
  const sa = String(a ?? '').trim();
  const sb = String(b ?? '').trim();
  const ta = THAI_LEAD.test(sa);
  const tb = THAI_LEAD.test(sb);
  if (ta !== tb) return ta ? -1 : 1;
  return (ta ? thCollator : enCollator).compare(sa, sb);
};

/**
 * เรียง options ของ dropdown แบบ ก-ฮ -> a-z โดยไม่แก้ array ต้นทาง
 *
 * - option ที่ value เป็นค่าว่าง (เช่น "ทั้งหมด" / "-- เลือก --") ถูกตรึงไว้บนสุดตามลำดับเดิม
 * - ถ้ามี field group (ที่ Select ใช้แสดงหัวกลุ่ม) จะเรียงกลุ่มก่อนแล้วค่อยเรียงในกลุ่ม
 * - ส่ง childKey มาเมื่อเป็นต้นไม้ (เช่น brands ที่มี children) จะเรียงลูกให้ด้วย
 *
 * @param {Array} list
 * @param {object} [config]
 * @param {string|Function} [config.by] - field/ฟังก์ชันที่ใช้เป็นข้อความเรียง
 * @param {string} [config.groupKey='group']
 * @param {string} [config.childKey]
 * @param {boolean} [config.pinEmpty=true] - ตรึง option ค่าว่างไว้บนสุด
 * @returns {Array} array ใหม่ที่เรียงแล้ว
 *
 * @example
 * {sortOptions(activeOptions(data_department, department_id)).map(...)}
 * {sortOptions(brands, { by: "name" }).map(...)}
 */
export const sortOptions = (list, config = {}) => {
  if (!Array.isArray(list)) return [];
  const { by, groupKey = 'group', childKey, pinEmpty = true } = config;

  const isPinned = (item) =>
    pinEmpty &&
    item !== null &&
    typeof item === 'object' &&
    'value' in item &&
    (item.value === '' || item.value === null || item.value === undefined);

  const pinned = [];
  const rest = [];
  list.forEach((item, idx) => (isPinned(item) ? pinned : rest).push({ item, idx }));

  rest.sort((a, b) => {
    const ga = a.item?.[groupKey];
    const gb = b.item?.[groupKey];
    if (!!ga !== !!gb) return ga ? 1 : -1; // ไม่มีกลุ่มมาก่อนกลุ่ม
    if (ga && gb) {
      const c = compareLabels(ga, gb);
      if (c !== 0) return c;
    }
    return compareLabels(optionLabel(a.item, by), optionLabel(b.item, by)) || a.idx - b.idx;
  });

  return [...pinned, ...rest].map(({ item }) =>
    childKey && Array.isArray(item?.[childKey])
      ? { ...item, [childKey]: sortOptions(item[childKey], config) }
      : item
  );
};

const helpers = {
  random_charactor,
  copyText,
  params,
  required,
  debounce,
  throttle,
  sleep,
  isEmpty,
  isOptionActive,
  activeOptions,
  activeTreeOptions,
  optionLabel,
  compareLabels,
  sortOptions,
};

export default helpers;
